/**
 * CategoryOverrideSheet — Plan 04-05 / UI-SPEC §"<CategoryOverrideSheet>".
 *
 * BottomSheet-mounted radio picker for the manual category override flow.
 * Renders 9 chips (8 CATEGORY_KEYS + 'ukategorisert') in a 3-column grid;
 * the chip whose value matches `item.category` (or `ukategorisert` when
 * `item.category` is null) gets `aria-checked="true"`.
 *
 * Behaviour contract (D-11 + UI-SPEC §"Component Inventory" #7):
 *   - Picking a chip fires updateItem.mutate with
 *     `{ category: <key|null>, category_overridden: true, version }` and
 *     closes the sheet immediately. WS broadcasts the move; peer clients
 *     animate cross-section re-flow.
 *   - Picking 'ukategorisert' sends `category: null` + override=true so
 *     the categoriser doesn't re-classify on next text edit.
 *   - "Tilbakestill kategori" link is rendered ONLY when
 *     `item.category_overridden === 1`. Click sends `category_overridden:
 *     false` with no `category` field — server re-runs the categoriser.
 *   - Sheet renders nothing when `item === null` (closed slot).
 *
 * The sheet's outer panel `data-testid="shopping-override-sheet"` lives on
 * a wrapper inside BottomSheet's children (BottomSheet itself sets
 * `data-testid="bottom-sheet-panel"` — we add a stable shopping-scoped id).
 *
 * #157 (redesign steg 7d, D9): fortsatt arket ⋯ åpner. Valgene har Chip-
 * utseendet (valgt fylt `bg-solid`, ellers omrisset glass), men beholder
 * radiosemantikken. Fargeprikkene er borte, som på kategorikortene.
 * Redigering og sletting av varen er ny oppførsel 6 og er ikke med.
 */
import { useTranslation } from 'react-i18next';
import { CATEGORY_KEYS, type CategoryKey } from '@stell/shared';
import type { ShoppingItem } from '@stell/shared';
import { BottomSheet } from '../../components/primitives/BottomSheet';
import { useShoppingMutations } from './useShoppingMutations';

interface Props {
  item: ShoppingItem | null;
  onClose: () => void;
}

type ChipKey = CategoryKey | 'ukategorisert';

const CHIP_KEYS: readonly ChipKey[] = [...CATEGORY_KEYS, 'ukategorisert'] as const;

export function CategoryOverrideSheet({ item, onClose }: Props) {
  const { t } = useTranslation();
  const { updateItem } = useShoppingMutations();

  if (!item) return null;

  const currentChipKey: ChipKey =
    item.category === null ? 'ukategorisert' : (item.category as ChipKey);

  function pickCategory(key: ChipKey) {
    if (!item) return;
    const category: CategoryKey | null = key === 'ukategorisert' ? null : key;
    updateItem.mutate({
      item,
      patch: { category, category_overridden: true },
    });
    onClose();
  }

  function reset() {
    if (!item) return;
    updateItem.mutate({
      item,
      // Intentionally NO `category` field — server re-runs the categoriser.
      patch: { category_overridden: false },
    });
    onClose();
  }

  return (
    <BottomSheet open={item !== null} onClose={onClose} title={t('shopping.override.title')}>
      <div data-testid="shopping-override-sheet">
        <div
          role="radiogroup"
          aria-label={t('shopping.override.title')}
          className="grid grid-cols-3 gap-2"
        >
          {CHIP_KEYS.map((key) => {
            const isSelected = key === currentChipKey;
            const labelKey =
              key === 'ukategorisert'
                ? 'shopping.category.ukategorisert'
                : `shopping.category.${key}`;
            return (
              <button
                key={key}
                type="button"
                role="radio"
                aria-checked={isSelected}
                data-testid={`shopping-override-chip-${key}`}
                onClick={() => pickCategory(key)}
                className={[
                  'min-h-11 min-w-11 px-3 py-2 rounded-md text-sm font-bold',
                  'flex items-center justify-center gap-2',
                  isSelected
                    ? 'bg-solid text-on-solid border border-transparent'
                    : 'bg-secondary text-ink border border-secondary-line hover:bg-inset',
                ].join(' ')}
              >
                <span className="text-center">{t(labelKey)}</span>
              </button>
            );
          })}
        </div>
        {item.category_overridden === 1 ? (
          <button
            type="button"
            data-testid="shopping-override-reset"
            onClick={reset}
            className="mt-3 min-h-11 w-full text-center text-sm font-extrabold text-primary underline"
          >
            {t('shopping.override.reset')}
          </button>
        ) : null}
      </div>
    </BottomSheet>
  );
}
